/**
 * Debug Page - Production-safe diagnostics
 * 
 * Displays runtime configuration, API settings, and environment info.
 * Protected route - requires authentication + admin role.
 * 
 * Access: /__debug (admin-only, or dev/staging only via env flag)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProviderUnified';
import { 
  API_BASE_URL, 
  isProduction, 
  isDevelopment, 
  getEnvironment,
  getApiTarget,
} from '../config/api.config';
import { Shield, AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

export default function DebugPage() {
  const { user } = useAuth();
  const [apiHealth, setApiHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch API health check
  const checkApiHealth = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json().catch(() => ({ status: 'unknown' }));
      
      setApiHealth({
        status: response.ok ? 'healthy' : 'unhealthy',
        statusCode: response.status,
        data: data,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      setApiHealth({
        status: 'error',
        error: error.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  useEffect(() => {
    checkApiHealth();
  }, []);

  // Get build metadata (if available)
  const buildInfo = {
    version: import.meta.env.VITE_APP_VERSION || 'unknown',
    buildTime: import.meta.env.VITE_BUILD_TIME || 'unknown',
    commitSha: import.meta.env.VITE_COMMIT_SHA || 'unknown',
    environment: getEnvironment(),
    mode: import.meta.env.MODE,
  };

  // Get API configuration
  const apiConfig = {
    baseUrl: API_BASE_URL,
    apiTarget: getApiTarget(),
    viteApiUrl: import.meta.env.VITE_API_URL || 'not set',
    viteApiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'not set',
    viteBackendUrl: import.meta.env.VITE_BACKEND_URL || 'not set',
  };

  // Get feature flags
  const featureFlags = {
    isProduction: isProduction(),
    isDevelopment: isDevelopment(),
  };

  // Get auth info
  const authInfo = {
    hasUser: !!user,
    userId: user?.id || user?._id || 'not available',
    userEmail: user?.email || 'not available',
    userRole: user?.role || 'not available',
    organizationId: user?.organizationId || 'not available',
    firebaseUid: user?.firebaseUid || 'not available',
  };

  // Validate production safety
  const productionSafety = {
    hasLocalhostInApiUrl: API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1'),
    hasHttpInApiUrl: API_BASE_URL.startsWith('http://'),
    isSecure: API_BASE_URL.startsWith('https://'),
    isValidProduction: isProduction() && API_BASE_URL.startsWith('https://') && !API_BASE_URL.includes('localhost'),
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case true:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'unhealthy':
      case false:
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Shield className="w-8 h-8 text-blue-600" />
                Production Diagnostics
              </h1>
              <p className="text-gray-600 mt-2">
                Runtime configuration and API health status
              </p>
            </div>
            <button
              onClick={checkApiHealth}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Last refreshed: {lastRefresh.toLocaleString()}
          </p>
        </div>

        {/* Production Safety Warnings */}
        {isProduction() && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h3 className="font-semibold text-yellow-800">Production Safety Check</h3>
                <div className="mt-2 space-y-1">
                  {productionSafety.hasLocalhostInApiUrl && (
                    <p className="text-sm text-yellow-700">
                      Warning: API URL contains localhost - this will be blocked in production
                    </p>
                  )}
                  {productionSafety.hasHttpInApiUrl && (
                    <p className="text-sm text-yellow-700">
                      Warning: API URL uses HTTP instead of HTTPS - mixed content will be blocked
                    </p>
                  )}
                  {productionSafety.isValidProduction && (
                    <p className="text-sm text-green-700">
                      Production configuration appears valid
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* API Health Status */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">API Health Check</h2>
          {loading ? (
            <div className="text-gray-500">Checking API health...</div>
          ) : apiHealth ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {getStatusIcon(apiHealth.status)}
                <span className="font-medium">
                  Status: <span className={apiHealth.status === 'healthy' ? 'text-green-600' : 'text-red-600'}>
                    {apiHealth.status.toUpperCase()}
                  </span>
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Status Code: {apiHealth.statusCode || 'N/A'}</p>
                <p>Checked at: {new Date(apiHealth.timestamp).toLocaleString()}</p>
                {apiHealth.error && (
                  <p className="text-red-600 mt-2">Error: {apiHealth.error}</p>
                )}
                {apiHealth.data && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-blue-600">Response Data</summary>
                    <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto">
                      {JSON.stringify(apiHealth.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">No health check data available</div>
          )}
        </div>

        {/* API Configuration */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">API Configuration</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Base URL:</span>
              <span className="font-mono text-gray-900">{apiConfig.baseUrl || 'not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">API Target:</span>
              <span className="font-mono text-gray-900">{apiConfig.apiTarget}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">VITE_API_URL:</span>
              <span className="font-mono text-gray-900">{apiConfig.viteApiUrl}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">VITE_API_BASE_URL:</span>
              <span className="font-mono text-gray-900">{apiConfig.viteApiBaseUrl}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">VITE_BACKEND_URL:</span>
              <span className="font-mono text-gray-900">{apiConfig.viteBackendUrl}</span>
            </div>
          </div>
        </div>

        {/* Environment Flags */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Environment</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Is Production:</span>
              <div className="flex items-center gap-2">
                {getStatusIcon(featureFlags.isProduction)}
                <span>{featureFlags.isProduction ? 'YES' : 'NO'}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Is Development:</span>
              <div className="flex items-center gap-2">
                {getStatusIcon(featureFlags.isDevelopment)}
                <span>{featureFlags.isDevelopment ? 'YES' : 'NO'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Build Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Build Information</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Version:</span>
              <span className="font-mono text-gray-900">{buildInfo.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Build Time:</span>
              <span className="font-mono text-gray-900">{buildInfo.buildTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Commit SHA:</span>
              <span className="font-mono text-gray-900">{buildInfo.commitSha}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Environment:</span>
              <span className="font-mono text-gray-900">{buildInfo.environment}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Mode:</span>
              <span className="font-mono text-gray-900">{buildInfo.mode}</span>
            </div>
          </div>
        </div>

        {/* Auth Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Authenticated:</span>
              {getStatusIcon(authInfo.hasUser)}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">User ID:</span>
              <span className="font-mono text-gray-900">{authInfo.userId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-mono text-gray-900">{authInfo.userEmail}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Role:</span>
              <span className="font-mono text-gray-900">{authInfo.userRole}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Organization ID:</span>
              <span className="font-mono text-gray-900">{authInfo.organizationId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Firebase UID:</span>
              <span className="font-mono text-gray-900">{authInfo.firebaseUid}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
